import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import { useIsAdmin } from '../lib/auth';
import { clearSwrCache, clearSwrCacheByPrefix, hasFreshSwrCache, swrLoad } from '../lib/swrCache';
import type { EventSeries, ExtractedEvent, SeriesType, Venue } from '../types';
import EventDetailModal from '../components/EventDetailModal';
import EventEditModal from '../components/EventEditModal';
import SeriesEditModal from '../components/SeriesEditModal';
import SubscribeButton from '../components/SubscribeButton';
import FavoriteButton from '../components/FavoriteButton';
import EventList, { EventDensityToggle, type EventListDensity } from '../components/EventList';
import { useRecalculateDate } from '../lib/useRecalculateDate';
import { compareEventDates } from '../lib/eventDates';
import { presetWindow } from '../lib/eventFilters';

const TYPE_LABELS: Record<SeriesType, string> = {
  'recurring-event': 'Recurring event',
  'aggregator': 'Aggregator',
  'organizer': 'Organizer',
};

const DENSITY_STORAGE_KEY = 'mh:eventListDensity';

function loadStoredDensity(): EventListDensity {
  if (typeof window === 'undefined') return 'compact';
  const v = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return v === 'cards' ? 'cards' : 'compact';
}

export default function SeriesDetailPage() {
  const isAdmin = useIsAdmin();
  const { seriesId } = useParams();
  const [series, setSeries] = useState<EventSeries | null>(null);
  const [events, setEvents] = useState<ExtractedEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExtractedEvent | null>(null);
  const [editingSeries, setEditingSeries] = useState<EventSeries | null>(null);
  const [editingEvent, setEditingEvent] = useState<ExtractedEvent | null>(null);
  const [adminVenues, setAdminVenues] = useState<Venue[]>([]);
  const [adminSeries, setAdminSeries] = useState<EventSeries[]>([]);
  const [density, setDensity] = useState<EventListDensity>(loadStoredDensity);
  const eventWindow = useMemo(() => {
    const window = presetWindow('all')!;
    const from = window.from.toISOString(), to = window.to.toISOString();
    return { from, to, key: `events:${from}|${to}` };
  }, []);

  const updateDensity = (d: EventListDensity) => {
    setDensity(d);
    if (typeof window !== 'undefined') window.localStorage.setItem(DENSITY_STORAGE_KEY, d);
  };

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api.get<Venue[]>('/api/venues/public')
      .then((vs) => { if (!cancelled) setAdminVenues(vs); })
      .catch(() => { /* non-blocking */ });
    api.get<EventSeries[]>('/api/mgmt/series')
      .then((ss) => { if (!cancelled) setAdminSeries(ss); })
      .catch(() => { /* non-blocking */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
    if (!seriesId) return;
    let cancelled = false;
    void swrLoad<EventSeries[]>({
      key: 'series:public',
      fetcher: () => api.get<EventSeries[]>('/api/series/public'),
      onData: (all) => setSeries(all.find((s) => s.rowKey === seriesId) ?? null),
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    const { from, to, key: eventsKey } = eventWindow;
    if (!hasFreshSwrCache(eventsKey)) setEvents(null);
    void swrLoad<ExtractedEvent[]>({
      key: eventsKey,
      fetcher: () => api.get<ExtractedEvent[]>(`/api/events/published?from=${from}&to=${to}`),
      onData: (data) => setEvents(data),
      onError: (e) => setError(String(e)),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, [seriesId, eventWindow]);

  const seriesEvents = useMemo(
    () => (events ?? []).filter((e) => e.seriesId === seriesId).sort(compareEventDates),
    [events, seriesId]
  );

  // Admin: per-event re-extract the original raw post (e.g. correct a wrong
  // date). Polls the same `events:from|to` window the page already loaded.
  const { recalculatingIds, markingWrongIds, recalculateDate, markWrongDate } = useRecalculateDate(
    // `fresh=1` skips the server's 60s response cache so the recalc poll and the
    // mark-wrong reconcile observe the just-applied edit instead of a stale snapshot.
    () => api.get<ExtractedEvent[]>(`/api/events/published?from=${eventWindow.from}&to=${eventWindow.to}&fresh=1`),
    setEvents,
    eventWindow.key,
  );

  return (
    <>
      <p className="small mb-2"><Link to="/series">← All series</Link></p>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {!series && !error && <LoadingBlock label="Loading series…" />}

      {series && (
        <header className="mb-4">
          <div className="d-flex align-items-start gap-2">
            <h1 className="mb-1 flex-grow-1">{series.name}</h1>
            {isAdmin && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setEditingSeries(series)}
                aria-label={`Edit ${series.name}`}
                title="Edit series"
              >
                <i className="bi bi-pencil-square me-1" aria-hidden="true" />
                Edit
              </button>
            )}
            <SubscribeButton target={{ kind: 'series', id: series.rowKey, name: series.name }} />
            <FavoriteButton kind="series" targetId={series.rowKey} targetName={series.name} />
          </div>
          <p className="text-muted mb-2">{TYPE_LABELS[series.partitionKey]}</p>
          {series.description && <p className="mb-2">{series.description}</p>}
          <p className="small d-flex flex-wrap gap-3 mb-0">
            {series.website && <a href={series.website} target="_blank" rel="noreferrer noopener">Website ↗</a>}
            {series.instagramHandle && (
              <a href={`https://instagram.com/${series.instagramHandle}`} target="_blank" rel="noreferrer noopener">
                @{series.instagramHandle} ↗
              </a>
            )}
            {series.facebookUrl && (
              <a href={series.facebookUrl} target="_blank" rel="noreferrer noopener">Facebook ↗</a>
            )}
          </p>
        </header>
      )}

      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h2 className="h5 mb-0">Upcoming{series ? ` – ${series.name}` : ''}</h2>
        {events !== null && seriesEvents.length > 0 && (
          <EventDensityToggle density={density} onChange={updateDensity} />
        )}
      </div>

      {events === null && <p>Loading events…</p>}
      {events && seriesEvents.length === 0 && (
        <div className="alert alert-info">No upcoming events captured for this series yet.</div>
      )}

      {events && seriesEvents.length > 0 && (
        <EventList
          items={seriesEvents}
          density={density}
          groupByDate
          hideSeries
          isAdmin={isAdmin}
          onSelect={setSelected}
          onEdit={(e) => setEditingEvent(e)}
          onRecalculateDate={isAdmin ? recalculateDate : undefined}
          recalculatingIds={recalculatingIds}
          onMarkWrongDate={isAdmin ? markWrongDate : undefined}
          markingWrongIds={markingWrongIds}
          onEventUpdated={isAdmin ? (updated) => setEvents((curr) => curr ? curr.map((x) => x.rowKey === updated.rowKey ? { ...x, ...updated } : x) : curr) : undefined}
          ariaLabel="Upcoming events in this series"
          cardColClassName="col-md-6"
        />
      )}

      <EventDetailModal
        event={selected}
        onClose={() => setSelected(null)}
        onEdit={isAdmin ? (e) => { setSelected(null); setEditingEvent(e); } : undefined}
        onEventUpdated={isAdmin ? (updated) => {
          setEvents((curr) => curr ? curr.map((x) => x.rowKey === updated.rowKey ? { ...x, ...updated } : x) : curr);
          setSelected((curr) => curr && curr.rowKey === updated.rowKey ? { ...curr, ...updated } : curr);
        } : undefined}
      />

      <SeriesEditModal
        series={editingSeries}
        onClose={() => setEditingSeries(null)}
        onSaved={(s) => {
          setSeries(s);
          clearSwrCache('series:public');
        }}
      />

      <EventEditModal
        event={editingEvent}
        venues={adminVenues}
        seriesList={adminSeries}
        onClose={() => setEditingEvent(null)}
        onSaved={(saved) => {
          setEvents((curr) => curr ? curr.map((e) => e.rowKey === saved.rowKey ? { ...e, ...saved } : e) : curr);
          clearSwrCacheByPrefix('events:');
        }}
      />
    </>
  );
}
